import { PracticeTheme } from '../types';

// Position group definitions for each sport
export const POSITION_GROUPS = {
  Football: ['QB', 'WR', 'OL', 'RB', 'DL', 'LB', 'DB'],
  Basketball: ['Guards', 'Forwards', 'Centers', 'Perimeter', 'Post'],
  Soccer: ['Goalkeepers', 'Defenders', 'Midfielders', 'Forwards'],
  Baseball: ['Pitchers', 'Catchers', 'Infielders', '1st Basemen', 'Outfielders'],
  Softball: ['Pitchers', 'Catchers', 'Infielders', '1st Basemen', 'Outfielders'],
  Volleyball: ['Setters', 'Outside Hitters', 'Middle Blockers', 'Opposite Hitters', 'Liberos'],
  Hockey: ['Goalies', 'Defensemen', 'Forwards', 'Centers', 'Wings'],
  Lacrosse: ['Goalies', 'Defensemen', 'Midfielders', 'Attackmen'],
} as const;

// Get position groups for a specific sport
export const getSportPositionGroups = (sport: string): string[] => {
  switch (sport.toLowerCase()) {
    case 'football':
    case 'flagfootball':
      return ["QB", "WR", "OL", "RB", "DL", "LB", "DB"];
    case 'basketball':
      return ["Guards", "Forwards", "Centers"];
    case 'baseball':
    case 'softball':
      return ["Pitchers", "Catchers", "Infielders", "1st Basemen", "Outfielders"];
    case 'soccer':
      return ["Goalkeepers", "Defenders", "Midfielders", "Forwards"];
    case 'volleyball':
      return ["Setters", "Hitters", "Middle Blockers", "Liberos"];
    case 'hockey':
      return ["Goalies", "Defensemen", "Centers", "Wings"];
    case 'lacrosse':
      return ["Attackmen", "Midfielders", "Defensemen", "Goalies"];
    default:
      return [];
  }
};

// Get focus areas based on practice theme
export const getFocusAreasForTheme = (theme?: string): string[] => {
  // Football themes
  if (['Offense Day', 'Team Day'].includes(theme || '')) {
    return ['Offense', 'Passing', 'Protection', 'Run Game'];
  } else if (['Defense Day'].includes(theme || '')) {
    return ['Defense', 'Coverage', 'Pass Rush', 'Run Defense'];
  } else if (['Special Teams Day'].includes(theme || '')) {
    return ['Special Teams', 'Kicking', 'Coverage', 'Returns'];
  } else if (['Conditioning Day'].includes(theme || '')) {
    return ['Conditioning', 'Speed', 'Strength', 'Agility'];
  } else if (['Skills Day'].includes(theme || '')) {
    return ['Fundamentals', 'Technique', 'Individual Skills'];
  } else if (['Scrimmage Day', 'Match Day'].includes(theme || '')) {
    return ['Team Competition', 'Game Situations', 'Execution'];
  }

  // Baseball/Softball themes
  if (['Hitting Day'].includes(theme || '')) {
    return ['Offense', 'Batting', 'Base Running'];
  } else if (['Fielding Day', 'Team Defense Day'].includes(theme || '')) {
    return ['Defense', 'Fielding', 'Throwing'];
  } else if (['Situational Day'].includes(theme || '')) {
    return ['Game Situations', 'Strategy', 'Execution'];
  }

  // Basketball themes
  if (['Shooting Day'].includes(theme || '')) {
    return ['Shooting', 'Scoring', 'Technique'];
  }

  // Soccer themes
  if (['Attacking Day'].includes(theme || '')) {
    return ['Attacking', 'Finishing', 'Creating Space'];
  } else if (['Defending Day'].includes(theme || '')) {
    return ['Defending', 'Pressing', 'Positioning'];
  } else if (['Possession Day'].includes(theme || '')) {
    return ['Possession', 'Passing', 'Movement'];
  } else if (['Set Pieces Day'].includes(theme || '')) {
    return ['Set Pieces', 'Corners', 'Free Kicks', 'Throw-ins'];
  }

  // Default
  return ['Team Execution'];
};

// Get practice themes for a specific sport
export const getThemesForSport = (sport?: string): Record<string, string> => {
  // Default football themes
  const footballThemes = {
    'Offense Day': 'Offense Day',
    'Defense Day': 'Defense Day',
    'Team Day': 'Team Day',
    'Game Day': 'Game Day'
  };

  // Sport-specific themes
  switch (sport) {
    case 'Baseball':
    case 'Softball':
      return {
        'Hitting Day': 'Hitting Day',
        'Fielding Day': 'Fielding Day',
        'Team Day': 'Team Day',
        'Scrimmage Day': 'Scrimmage Day'
      };
    case 'Basketball':
      return {
        'Offense Day': 'Offense Day',
        'Defense Day': 'Defense Day',
        'Team Day': 'Team Day',
        'Skills Day': 'Skills Day',
        'Conditioning Day': 'Conditioning Day',
        'Shooting Day': 'Shooting Day',
        'Scrimmage Day': 'Scrimmage Day'
      };
    case 'Soccer':
      return {
        'Attacking Day': 'Attacking Day',
        'Defending Day': 'Defending Day',
        'Possession Day': 'Possession Day',
        'Team Day': 'Team Day',
        'Set Pieces Day': 'Set Pieces Day',
        'Skills Day': 'Skills Day',
        'Match Day': 'Match Day'
      };
    default:
      return footballThemes;
  }
};

// Determine if position groups should be shown based on theme and focus area
export const shouldShowPositionForTheme = (
  position: string, 
  theme: PracticeTheme | string, 
  focusArea: string, 
  sport?: string
): boolean => {
  // Handle "ALL" position group which is always shown
  if (position === "ALL") return true;

  // If no sport is provided or theme is not provided, default to showing the position
  if (!sport || !theme) return true;

  const lowerTheme = theme.toLowerCase();

  // Get sport-specific position groups
  const positionGroups = getSportPositionGroups(sport);

  // If position is not in the sport's position groups, don't show it
  if (!positionGroups.includes(position)) return false;

  // Handle common themes across sports
  if (lowerTheme.includes('conditioning day')) {
    return false; // Conditioning days don't need position drills
  }

  // For Team Day, show position-specific drills if the focus area is not "Team"
  if (lowerTheme.includes('team day')) {
    return focusArea.toLowerCase() !== 'team';
  }

  // Sport-specific logic for showing position groups
  switch (sport.toLowerCase()) {
    case 'football':
    case 'flagfootball':
      // Football: Show positions for offense/defense/special teams/skills days
      return lowerTheme.includes('offense day') ||
        lowerTheme.includes('defense day') ||
        lowerTheme.includes('special teams day') ||
        lowerTheme.includes('skills day');

    case 'basketball':
      // Basketball: Show positions for shooting/skills days
      return lowerTheme.includes('shooting day') ||
        lowerTheme.includes('skills day');

    case 'baseball':
    case 'softball':
      // Baseball/Softball: Show positions for hitting/fielding/defense days
      return lowerTheme.includes('hitting day') ||
        lowerTheme.includes('fielding day') ||
        lowerTheme.includes('team defense day');

    case 'soccer':
      // Soccer: Show positions for attacking/defending/possession days
      return lowerTheme.includes('attacking day') ||
        lowerTheme.includes('defending day') ||
        lowerTheme.includes('possession day');

    case 'volleyball':
      // Volleyball: Show positions for serving/passing days
      return lowerTheme.includes('serving day') ||
        lowerTheme.includes('passing day');

    case 'hockey':
      // Hockey: Show positions for skills day
      return lowerTheme.includes('skills day');

    case 'lacrosse':
      // Lacrosse: Show positions for skills/transition days
      return lowerTheme.includes('skills day') ||
        lowerTheme.includes('transition day');

    default:
      return lowerTheme.includes('skills day'); // Default to showing for skills days only
  }
};

// Check if a step name matches a position group
export const matchesPositionGroup = (stepName: string, group: string, sport?: string): boolean => {
  if (!stepName) return false;

  const name = stepName.toLowerCase();
  const lowerGroup = group.toLowerCase();

  // Direct matches (standard across all sports)
  if (
    name.startsWith(`${lowerGroup}:`) ||
    name.startsWith(`${lowerGroup} `) ||
    name === lowerGroup
  ) {
    return true;
  }

  // Sport-specific matching logic
  if (sport) {
    switch (sport.toLowerCase()) {
      case 'football':
      case 'flagfootball':
        // Football-specific position matching
        if (group === 'QB' && (name.includes('quarterback') || name.includes('qb'))) {
          return true;
        }
        if (group === 'WR' && (name.includes('wide receiver') || name.includes('wr'))) {
          return true;
        }
        if (group === 'OL' && (name.includes('offensive line') || name.includes('o-line') || name.includes('ol'))) {
          return true;
        }
        if (group === 'RB' && (name.includes('running back') || name.includes('rb') || name.includes('halfback') || name.includes('fullback'))) {
          return true;
        }
        if (group === 'DL' && (name.includes('defensive line') || name.includes('d-line') || name.includes('dl'))) {
          return true;
        }
        if (group === 'LB' && (name.includes('linebacker') || name.includes('lb'))) {
          return true;
        }
        if (group === 'DB' && (name.includes('defensive back') || name.includes('db') || name.includes('cornerback') || name.includes('safety'))) {
          return true;
        }
        break;

      case 'basketball':
        // Basketball-specific position matching
        if (group === 'Guards' && (name.includes('guard') || name.includes('point') || name.includes('shooting guard'))) {
          return true;
        }
        if (group === 'Forwards' && (name.includes('forward') || name.includes('small forward') || name.includes('power forward'))) {
          return true;
        }
        if (group === 'Centers' && name.includes('center')) {
          return true;
        }
        break;

      case 'baseball':
      case 'softball':
        // Baseball/Softball-specific position matching
        if (group === 'Pitchers' && (name.includes('pitcher') || name.includes('mound'))) {
          return true;
        }
        if (group === 'Catchers' && name.includes('catcher')) {
          return true;
        }
        if (group === '1st Basemen' && (name.includes('first base') || name.includes('1st base') || 
          name.includes('first baseman') || name.includes('1st baseman'))) {
          return true;
        }
        if (group === 'Infielders' && (name.includes('infield') || name.includes('second') ||
          name.includes('third') || name.includes('shortstop') || 
          (name.includes('baseman') && !name.includes('first') && !name.includes('1st')))) {
          return true;
        }
        if (group === 'Outfielders' && (name.includes('outfield') || name.includes('left field') ||
          name.includes('center field') || name.includes('right field'))) {
          return true;
        }
        break;
    }
  }

  // Fallback: partial match for the position name
  return name.includes(lowerGroup);
};